import React from 'react';
import { connect, MapDispatchToProps, MapStateToProps } from 'react-redux';
import { RouteComponentProps } from '@reach/router';

import { RootState } from 'store/store';
import { Box } from '../../../utils/style/extendable';
import styled from 'styled-components';

import {
  requireProviderTags,
  requireProviderTagsFn,
} from 'store/providers/providerSlice';
import { getAllTopics, getAllTopicsFn } from 'store/pdp/pdpSlice';
import { appDispatch } from 'store/store';
import { CompanyPDP, ICompany } from 'store/companies/companiesTypes';
import { IThisUserState } from 'store/thisUser/thisUserSlice';
import { ITags } from 'store/providers/providerTypes';

interface IStateToProps {
  company: ICompany;
  isPDPAdmin: boolean;
  allTopicsLoaded?: boolean;
  thisUser: IThisUserState;
  companyProvidersId: number[];
  allTags: { [key: number]: ITags | null };
}

interface IDispatchToProps {
  requireProviderTags: (id: number) => ReturnType<typeof requireProviderTagsFn>;
  getAllTopics: (pdpId: number) => ReturnType<typeof getAllTopicsFn>;
}

interface IOwnProps {
  companyId?: string;
  pdpId?: string;
  children: React.ReactNode;
}

const ConfigBox = styled(Box)`
  height: 100%;
  overflow: auto;
  display: flex;

  & > div {
    max-width: 100%;
  }

  @media screen and (max-width: 1100px) {
    margin-inline-start: 1.5rem;
  }

  @media screen and (max-width: 600px) {
    margin-inline-start: 0;
  }
`;
type IComponentProps = IOwnProps &
  IStateToProps &
  RouteComponentProps &
  IDispatchToProps;

class PDPPanel extends React.Component<IComponentProps> {
  componentDidMount() {
    this.props.companyProvidersId.forEach((id) => {
      if (this.props.allTags[id]?.status !== 'loaded') {
        this.props.requireProviderTags(id);
      }
    });
    if (!this.props.allTopicsLoaded && this.props.pdpId)
      this.props.getAllTopics(Number(this.props.pdpId));
  }

  componentDidUpdate(prevProps: Readonly<IComponentProps>) {
    const pdpId = this.props.pdpId && Number(this.props.pdpId);
    if (pdpId && pdpId !== Number(prevProps.pdpId))
      this.props.getAllTopics(pdpId);
  }

  render() {
    const { children, pdpId, companyId } = this.props;

    const childrenWithProps = React.Children.map(children, (child) => {
      if (React.isValidElement(child)) {
        return React.cloneElement(child as any, {
          pdpId,
          companyId,
          key: `${companyId}-${pdpId}`,
        });
      }
      return child;
    });

    return (
      <React.Fragment>
        <>
          <ConfigBox
            style={{
              padding: 0,
              width: '100%',
              height: '100%',
            }}
          >
            {childrenWithProps}
          </ConfigBox>
        </>
      </React.Fragment>
    );
  }
}

const mapStateToProps: MapStateToProps<IStateToProps, IOwnProps, RootState> = (
  state,
  props
) => {
  const companyId = Number(props.companyId);

  const thisUser = state.thisUser;
  const company = state.companies.data[companyId];
  const selectedPdpPanel = company?.pdpPanels?.find(
    (el: CompanyPDP) => el.id === Number(props.pdpId)
  );

  // const isCompanyOwner =
  //   !!Number(thisUser.userId) &&
  //   Number(thisUser.userId) === company?.owner.userId;
  //
  // const isSupport = !!Object.values(
  //   company.members[Number(thisUser.userId)].roles
  // ).find((it: any) => it.name.includes(SupportRole));

  const isPDPAdmin = !!Object.values(
    company.members[Number(thisUser.userId)].roles
  ).find((it: any) => it.id === selectedPdpPanel?.managerRoleId);

  const companyProvidersId = Object.values(state.providers.map).reduce(
    (current: any, item: any) => {
      if (item.data?.managingCompanyId === companyId)
        current.push(item.data.id);
      return current;
    },
    []
  );

  const allTags = state.providers.tags;

  const allTopicsLoaded = selectedPdpPanel?.id
    ? state.pdp.pdpMap?.[selectedPdpPanel.id]?.allTopicsLoaded
    : false;

  return {
    companyProvidersId,
    thisUser,
    company,
    isPDPAdmin,
    allTags,
    allTopicsLoaded,
  };
};
const mapDispatchToProps: MapDispatchToProps<
  IDispatchToProps,
  IOwnProps
> = () => {
  return {
    requireProviderTags: (id: number) =>
      appDispatch(requireProviderTags({ providerId: id })),
    getAllTopics: (pdpId: number) => appDispatch(getAllTopics({ pdpId })),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(PDPPanel);
